import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit'
import { enhancedTmsApiService } from '#api/enhancements/enhancedTmsApiService'
import type { EnhancedMarketId } from '#api/enhancements/enhancedTmsApiTypes'
import type {
  DeleteApiAdmintoolMarketsByMarketIdFactoryCodeAttrsAndFactoryCodeItemIdApiArg,
  FamiliesTreeResponseBody,
  PostApiAdmintoolMarketsByMarketIdFactoryCodeAttrsAndFactoryCodeItemIdApiArg,
  PostApiAdmintoolMarketsByMarketIdFactoryCodeAttrsAndFactoryCodeItemIdApiResponse,
  PutApiAdmintoolMarketsByMarketIdFactoryCodeAttrsAndFactoryCodeItemIdApiArg,
  PutApiAdmintoolMarketsByMarketIdFactoryCodeAttrsAndFactoryCodeItemIdApiResponse,
} from '#api/tmsApiService.ts'
import { makeSerializedError } from '#features/ErrorHandling/errorHelpers'
import {
  _pushOrReplaceFactoryCodeAttribute,
  _setMarketFactoryCodeAttributes,
  _setMarketFactoryCodeAttributesStatus,
  _setMarketFamilies,
  _setMarketFamiliesStatus,
  loadFactoryCodeAttributesForMarket,
  loadFamiliesForMarketAndLanguage,
} from '#store/factoryCodesSlice.ts'
import { store } from '#store/root'

export const factoryCodesObserver = (() => {
  const middleware = createListenerMiddleware()
  middleware.startListening({
    matcher: isAnyOf(loadFamiliesForMarketAndLanguage),
    effect: async (action, _listenerApi) => {
      const state = store.getState()
      if (loadFamiliesForMarketAndLanguage.match(action)) {
        const marketId = action.payload?.marketId
        const languageId = action.payload?.languageId
        const viewId = action.payload?.viewId

        if (!marketId || !languageId || !viewId) {
          return
        }

        // console.log('Request to load factory codes for market', marketId, 'language', languageId, 'forceReload:', action.payload?.forceReload)

        if (state.factoryCodes.byMarket[marketId]?.familiesByLanguage[languageId]?.status === 'loaded' && !action.payload?.forceReload) {
          return
        }

        try {
          store.dispatch(_setMarketFamiliesStatus({ marketId, languageId, status: 'loading' }))
          const request = store.dispatch(
            enhancedTmsApiService.endpoints.getApiProductdataMarketByMarketIdLanguageAndLanguageIdFamilies.initiate({
              marketId,
              languageId,
              // includeOnlyLocalCodes: false,
              viewId,
            }),
          )
          const result = await request.unwrap()
          // console.log('Loaded factory codes for market', marketId, result)
          const treeResult = result.families_result as FamiliesTreeResponseBody | undefined
          if (treeResult?.response_type !== 'listOfMainHeaders') {
            throw new Error('Expected tree of families, got list of families')
          }

          store.dispatch(_setMarketFamilies({ marketId, languageId, productData: treeResult.listOfMainHeaders }))
          store.dispatch(_setMarketFamiliesStatus({ marketId, languageId, status: 'loaded' }))
        } catch (e) {
          store.dispatch(_setMarketFamiliesStatus({ marketId, languageId, status: 'failed', error: makeSerializedError(e) }))
        }
      }
    },
  })

  middleware.startListening({
    matcher: isAnyOf(loadFactoryCodeAttributesForMarket),
    effect: async (action, _listenerApi) => {
      const state = store.getState()
      if (loadFactoryCodeAttributesForMarket.match(action)) {
        const marketId = action.payload?.marketId
        if (!marketId) {
          return
        }

        // console.log('Request to load factory code attributes for market', marketId, 'forceReload:', action.payload?.forceReload)

        if (state.factoryCodes.byMarket[marketId]?.factoryCodeAttributes.status === 'loaded' && !action.payload?.forceReload) {
          return
        }

        try {
          store.dispatch(_setMarketFactoryCodeAttributesStatus({ marketId, status: 'loading' }))
          const request = store.dispatch(
            enhancedTmsApiService.endpoints.getApiAdmintoolMarketsByMarketIdFactoryCodeAttrs.initiate({
              marketId,
            }),
          )
          const result = await request.unwrap()
          // console.log('Loaded factory code attributes for market', marketId, result)

          if (!result.listOfFactoryCodeAttributes) {
            throw new Error('Expected factory code attributes, got none')
          }

          store.dispatch(_setMarketFactoryCodeAttributes({ marketId, attributes: result.listOfFactoryCodeAttributes }))
        } catch (e) {
          store.dispatch(
            _setMarketFactoryCodeAttributesStatus({
              marketId,
              status: 'failed',
              error: makeSerializedError(e),
            }),
          )
        }
      }
    },
  })

  middleware.startListening({
    matcher: enhancedTmsApiService.endpoints.postApiAdmintoolMarketsByMarketIdFactoryCodeAttrsAndFactoryCodeItemId.matchFulfilled,
    effect: (action, api) => {
      console.log('Done POST /admintool/factoryCodeAttrs', action, api)
      const originalArgs = action.meta.arg.originalArgs as PostApiAdmintoolMarketsByMarketIdFactoryCodeAttrsAndFactoryCodeItemIdApiArg
      const result = action.payload as PostApiAdmintoolMarketsByMarketIdFactoryCodeAttrsAndFactoryCodeItemIdApiResponse

      if (!result.factoryCodeAttributes) {
        console.warn('No reference truck returned from POST /admintool/factoryCodeAttrs')
        return
      }

      api.dispatch(
        _pushOrReplaceFactoryCodeAttribute({
          marketId: originalArgs.marketId as EnhancedMarketId,
          code: result.factoryCodeAttributes.code,
          attribute: result.factoryCodeAttributes,
        }),
      )
    },
  })

  middleware.startListening({
    matcher: enhancedTmsApiService.endpoints.putApiAdmintoolMarketsByMarketIdFactoryCodeAttrsAndFactoryCodeItemId.matchFulfilled,
    effect: (action, api) => {
      console.log('Done PUT /admintool/factoryCodeAttrs', action, api)
      const originalArgs = action.meta.arg.originalArgs as PutApiAdmintoolMarketsByMarketIdFactoryCodeAttrsAndFactoryCodeItemIdApiArg
      const result = action.payload as PutApiAdmintoolMarketsByMarketIdFactoryCodeAttrsAndFactoryCodeItemIdApiResponse

      if (!result.factoryCodeAttributes) {
        console.warn('No reference truck returned from PUT /admintool/factoryCodeAttrs')
        return
      }

      api.dispatch(
        _pushOrReplaceFactoryCodeAttribute({
          marketId: originalArgs.marketId as EnhancedMarketId,
          code: originalArgs.factoryCodeItemId,
          attribute: result.factoryCodeAttributes,
        }),
      )
    },
  })

  middleware.startListening({
    matcher: enhancedTmsApiService.endpoints.deleteApiAdmintoolMarketsByMarketIdFactoryCodeAttrsAndFactoryCodeItemId.matchFulfilled,
    effect: (action, api) => {
      console.log('Done DELETE /admintool/factoryCodeAttrs', action, api)
      const originalArgs = action.meta.arg.originalArgs as DeleteApiAdmintoolMarketsByMarketIdFactoryCodeAttrsAndFactoryCodeItemIdApiArg
      api.dispatch(
        _pushOrReplaceFactoryCodeAttribute({
          marketId: originalArgs.marketId as EnhancedMarketId,
          code: originalArgs.factoryCodeItemId,
          attribute: null,
        }),
      )
    },
  })

  return middleware
})()
